import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import { containFitScale, isDesktopHost } from '../../../../infrastructure/runtime';
import { useAppFit } from '../../../hooks/useAppFit';
import {
  getRunnerForwardSpeed,
  startRunnerLevelUseCase,
} from '../../../../application/runner/runnerUseCases';
import { buildRunnerLevelFromLayout } from '../../../../domain/runner/buildRunnerLevelFromLayout';
import type { RunnerLevel, RunnerPlayer, VerticalMoveDirection } from '../../../../domain/runner/runnerTypes';
import { getItemsLayoutForGiant } from '../../../../domain/level/giantItemsLayouts';
import type { CharacterId } from '../../../../domain/ids/ids';
import type { GiantId } from '../../../../domain/ids/ids';
import { decideSecondChanceGate } from '../../../../domain';
import type { LevelRunSnapshot } from '../../../store/settleLevelRun';
import { playRunnerFinish } from '../../../assets/preloadSounds';
import {
  FINISH_DECEL_MS,
  LEVEL_COUNTDOWN_STEP_MS,
  LEVEL_COUNTDOWN_STEPS,
} from './gameplayConstants';
import { LevelCountdownOverlay } from './LevelCountdownOverlay';
import { LevelTutorialHintOverlay } from './LevelTutorialHintOverlay';
import { LevelWillyBlurOverlay } from './LevelWillyBlurOverlay';
import { LevelMonaTentacleOverlay } from './LevelMonaTentacleOverlay';
import { LevelDuckBallOverlay } from './LevelDuckBallOverlay';
import { LevelRozaCloudOverlay } from './LevelRozaCloudOverlay';
import { LevelSerjOverlay } from './LevelSerjOverlay';
import { LevelSvamiOverlay } from './LevelSvamiOverlay';
import { LevelSolOverlay } from './LevelSolOverlay';
import { LevelFactorOverlay } from './LevelFactorOverlay';
import { LevelMuOverlay } from './LevelMuOverlay';
import { LevelYettiOverlay } from './LevelYettiOverlay';
import { LevelDrakochiOverlay } from './LevelDrakochiOverlay';
import { LevelPlayfield } from './LevelPlayfield';
import { useRunnerLevelTick } from './hooks/useRunnerLevelTick';
import { useHoldFlightInput } from './hooks/useHoldFlightInput';
import { useRunnerTutorial } from './hooks/useRunnerTutorial';
import type { PlayfieldMotionBindings } from './playfieldMotionBindings';
import { smoothPlayerTilt } from './playerTilt';

const FINISH_DECEL_SEC = FINISH_DECEL_MS / 1000;

function nextCountdownLabel(current: string): string | null {
  const index = LEVEL_COUNTDOWN_STEPS.indexOf(
    current as (typeof LEVEL_COUNTDOWN_STEPS)[number],
  );
  if (index < 0 || index >= LEVEL_COUNTDOWN_STEPS.length - 1) {
    return null;
  }
  return LEVEL_COUNTDOWN_STEPS[index + 1];
}

export type LevelGameplayProps = {
  giantId: GiantId;
  orderIndex: number;
  characterId: CharacterId;
  isPaused: boolean;
  /** Increment to restore full lives after a successful second-chance minigame. */
  secondChanceRestoreKey?: number;
  motionBindingsRef: RefObject<PlayfieldMotionBindings | null>;
  onHudUpdate: (state: {
    health: number;
    maxHealth: number;
    collectiblesCollected: number;
    collectiblesTotal: number;
  }) => void;
  onSuccess: (snapshot: LevelRunSnapshot) => void;
  onFail: (snapshot: LevelRunSnapshot) => void;
  /** First time lives hit 0 — open second-chance minigame instead of failing. */
  onSecondChanceRequest?: (snapshot: LevelRunSnapshot) => void;
  onRunSnapshotUpdate?: (snapshot: LevelRunSnapshot) => void;
};

export function LevelGameplay({
  giantId,
  orderIndex,
  characterId,
  isPaused,
  secondChanceRestoreKey = 0,
  motionBindingsRef,
  onHudUpdate,
  onSuccess,
  onFail,
  onSecondChanceRequest,
  onRunSnapshotUpdate,
}: LevelGameplayProps) {
  const fit = useAppFit();
  const contain = containFitScale(fit.scale);
  const containStyle = isDesktopHost(fit.size)
    ? ({
        '--fit-contain-inv-x': String(contain.invX),
        '--fit-contain-inv-y': String(contain.invY),
        '--fit-contain-uniform': String(contain.uniform),
      } as CSSProperties)
    : undefined;
  const layout = useMemo(
    () => getItemsLayoutForGiant(giantId, orderIndex),
    [giantId, orderIndex],
  );
  const [level, setLevel] = useState<RunnerLevel | null>(() =>
    buildRunnerLevelFromLayout(layout),
  );
  const [player, setPlayer] = useState<RunnerPlayer | null>(() => {
    const built = buildRunnerLevelFromLayout(layout);
    return startRunnerLevelUseCase(built, characterId).player;
  });
  const [collectiblesCollected, setCollectiblesCollected] = useState(0);
  const [speedBoostersCollected, setSpeedBoostersCollected] = useState(0);
  const speedBoostersTotal = useMemo(
    () => layout.boosters.filter((booster) => booster.type === 'speed').length,
    [layout],
  );
  const [displayTilt] = useState(0);
  const [isFinishing, setIsFinishing] = useState(false);
  const elapsedSecondsRef = useRef(0);
  const collectiblesCollectedRef = useRef(0);
  const speedBoostersCollectedRef = useRef(0);
  const {
    hint: tutorialHint,
    skipCountdown,
    dismiss: dismissTutorialHint,
    resetForNewAttempt: resetTutorial,
    onObstacleHit,
    onBoosterCollected,
    onCollectibleCollected,
    onLivesDepleted,
  } = useRunnerTutorial();
  const onLivesDepletedRef = useRef(onLivesDepleted);
  onLivesDepletedRef.current = onLivesDepleted;
  const [countdownLabel, setCountdownLabel] = useState<string | null>(() =>
    skipCountdown ? null : LEVEL_COUNTDOWN_STEPS[0],
  );
  const [willyBlurActive, setWillyBlurActive] = useState(false);
  const willyBlurStartedRef = useRef(false);
  const [monaTentacleActive, setMonaTentacleActive] = useState(false);
  const monaTentacleStartedRef = useRef(false);
  const [duckBallActive, setDuckBallActive] = useState(false);
  const duckBallStartedRef = useRef(false);
  const [rozaCloudActive, setRozaCloudActive] = useState(false);
  const rozaCloudStartedRef = useRef(false);
  const [serjOverlayActive, setSerjOverlayActive] = useState(false);
  const serjOverlayStartedRef = useRef(false);
  const [svamiOverlayActive, setSvamiOverlayActive] = useState(false);
  const svamiOverlayStartedRef = useRef(false);
  const [solOverlayActive, setSolOverlayActive] = useState(false);
  const solOverlayStartedRef = useRef(false);
  const [factorOverlayActive, setFactorOverlayActive] = useState(false);
  const factorOverlayStartedRef = useRef(false);
  const [muOverlayActive, setMuOverlayActive] = useState(false);
  const muOverlayStartedRef = useRef(false);
  const [yettiOverlayActive, setYettiOverlayActive] = useState(false);
  const yettiOverlayStartedRef = useRef(false);
  const [drakochiOverlayActive, setDrakochiOverlayActive] = useState(false);
  const drakochiOverlayStartedRef = useRef(false);
  const collisionCountRef = useRef(0);
  const prevLivesRef = useRef<number | null>(null);
  /** Continuous sim — RAF source of truth (not overwritten by React each render). */
  const simLevelRef = useRef<RunnerLevel | null>(level);
  const simPlayerRef = useRef<RunnerPlayer | null>(player);
  const motionScaleRef = useRef(1);
  const isFinishingRef = useRef(false);
  const finishElapsedRef = useRef(0);
  const finishTiltRef = useRef(0);
  const displayTiltRef = useRef(0);
  const finishReportedRef = useRef(false);
  const finishSoundPlayedRef = useRef(false);
  const failReportedRef = useRef(false);
  const secondChanceUsedRef = useRef(false);
  const secondChanceRequestedRef = useRef(false);
  const pendingSecondChanceAfterHintRef = useRef(false);
  const lastRestoreKeyRef = useRef(secondChanceRestoreKey);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onFailRef = useRef(onFail);
  onFailRef.current = onFail;
  const onSecondChanceRequestRef = useRef(onSecondChanceRequest);
  onSecondChanceRequestRef.current = onSecondChanceRequest;
  const buildSnapshotRef = useRef<() => LevelRunSnapshot>(() => ({
    elapsedSec: 0,
    sparksCollected: 0,
    sparksTotal: 0,
    speedBoostersCollected: 0,
    speedBoostersTotal: 0,
    collisions: 0,
  }));

  useEffect(() => {
    const built = buildRunnerLevelFromLayout(layout);
    const session = startRunnerLevelUseCase(built, characterId);
    setLevel(session.level);
    setPlayer(session.player);
    simLevelRef.current = session.level;
    simPlayerRef.current = session.player;
    elapsedSecondsRef.current = 0;
    collectiblesCollectedRef.current = 0;
    speedBoostersCollectedRef.current = 0;
    setCollectiblesCollected(0);
    setSpeedBoostersCollected(0);
    setIsFinishing(false);
    setCountdownLabel(skipCountdown ? null : LEVEL_COUNTDOWN_STEPS[0]);
    setWillyBlurActive(false);
    willyBlurStartedRef.current = false;
    setMonaTentacleActive(false);
    monaTentacleStartedRef.current = false;
    setDuckBallActive(false);
    duckBallStartedRef.current = false;
    setRozaCloudActive(false);
    rozaCloudStartedRef.current = false;
    setSerjOverlayActive(false);
    serjOverlayStartedRef.current = false;
    setSvamiOverlayActive(false);
    svamiOverlayStartedRef.current = false;
    setSolOverlayActive(false);
    solOverlayStartedRef.current = false;
    setFactorOverlayActive(false);
    factorOverlayStartedRef.current = false;
    setMuOverlayActive(false);
    muOverlayStartedRef.current = false;
    setYettiOverlayActive(false);
    yettiOverlayStartedRef.current = false;
    setDrakochiOverlayActive(false);
    drakochiOverlayStartedRef.current = false;
    resetTutorial();
    collisionCountRef.current = 0;
    prevLivesRef.current = session.player.lives;
    motionScaleRef.current = 1;
    isFinishingRef.current = false;
    finishElapsedRef.current = 0;
    finishTiltRef.current = 0;
    displayTiltRef.current = 0;
    finishReportedRef.current = false;
    finishSoundPlayedRef.current = false;
    failReportedRef.current = false;
    secondChanceUsedRef.current = false;
    secondChanceRequestedRef.current = false;
    pendingSecondChanceAfterHintRef.current = false;
    // Sync without applying restore when the level attempt restarts.
    lastRestoreKeyRef.current = secondChanceRestoreKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restore key must not retrigger a full level reset
  }, [layout, characterId, giantId, orderIndex, skipCountdown, resetTutorial]);

  const isCountingDown = countdownLabel != null;
  const motionFrozen = isPaused || isCountingDown || tutorialHint != null;
  const inputEnabled =
    !isFinishing &&
    !isPaused &&
    !isCountingDown &&
    (tutorialHint == null || tutorialHint === 'controls');

  useEffect(() => {
    if (countdownLabel == null || isPaused) {
      return;
    }
    const timerId = window.setTimeout(() => {
      setCountdownLabel((current) =>
        current == null ? null : nextCountdownLabel(current),
      );
    }, LEVEL_COUNTDOWN_STEP_MS);
    return () => {
      window.clearTimeout(timerId);
    };
  }, [countdownLabel, isPaused]);

  useEffect(() => {
    if (
      giantId !== 'willy' ||
      level?.modifierZone?.entered !== true ||
      willyBlurStartedRef.current
    ) {
      return;
    }
    willyBlurStartedRef.current = true;
    setWillyBlurActive(true);
  }, [giantId, level?.modifierZone?.entered]);

  const onWillyBlurComplete = useCallback(() => {
    setWillyBlurActive(false);
  }, []);

  useEffect(() => {
    if (
      giantId !== 'mona' ||
      level?.modifierZone?.entered !== true ||
      monaTentacleStartedRef.current
    ) {
      return;
    }
    monaTentacleStartedRef.current = true;
    setMonaTentacleActive(true);
  }, [giantId, level?.modifierZone?.entered]);

  const onMonaTentacleComplete = useCallback(() => {
    setMonaTentacleActive(false);
  }, []);

  useEffect(() => {
    if (
      giantId !== 'duck' ||
      level?.modifierZone?.entered !== true ||
      duckBallStartedRef.current
    ) {
      return;
    }
    duckBallStartedRef.current = true;
    setDuckBallActive(true);
  }, [giantId, level?.modifierZone?.entered]);

  const onDuckBallComplete = useCallback(() => {
    setDuckBallActive(false);
  }, []);

  useEffect(() => {
    if (
      giantId !== 'roza' ||
      level?.modifierZone?.entered !== true ||
      rozaCloudStartedRef.current
    ) {
      return;
    }
    rozaCloudStartedRef.current = true;
    setRozaCloudActive(true);
  }, [giantId, level?.modifierZone?.entered]);

  const onRozaCloudComplete = useCallback(() => {
    setRozaCloudActive(false);
  }, []);

  useEffect(() => {
    if (
      giantId !== 'serj' ||
      level?.modifierZone?.entered !== true ||
      serjOverlayStartedRef.current
    ) {
      return;
    }
    serjOverlayStartedRef.current = true;
    setSerjOverlayActive(true);
  }, [giantId, level?.modifierZone?.entered]);

  const onSerjOverlayComplete = useCallback(() => {
    setSerjOverlayActive(false);
  }, []);

  useEffect(() => {
    if (
      giantId !== 'svami' ||
      level?.modifierZone?.entered !== true ||
      svamiOverlayStartedRef.current
    ) {
      return;
    }
    svamiOverlayStartedRef.current = true;
    setSvamiOverlayActive(true);
  }, [giantId, level?.modifierZone?.entered]);

  const onSvamiOverlayComplete = useCallback(() => {
    setSvamiOverlayActive(false);
  }, []);

  useEffect(() => {
    if (
      giantId !== 'sol' ||
      level?.modifierZone?.entered !== true ||
      solOverlayStartedRef.current
    ) {
      return;
    }
    solOverlayStartedRef.current = true;
    setSolOverlayActive(true);
  }, [giantId, level?.modifierZone?.entered]);

  const onSolOverlayComplete = useCallback(() => {
    setSolOverlayActive(false);
  }, []);

  useEffect(() => {
    if (
      giantId !== 'factor' ||
      level?.modifierZone?.entered !== true ||
      factorOverlayStartedRef.current
    ) {
      return;
    }
    factorOverlayStartedRef.current = true;
    setFactorOverlayActive(true);
  }, [giantId, level?.modifierZone?.entered]);

  const onFactorOverlayComplete = useCallback(() => {
    setFactorOverlayActive(false);
  }, []);

  useEffect(() => {
    if (
      giantId !== 'mu' ||
      level?.modifierZone?.entered !== true ||
      muOverlayStartedRef.current
    ) {
      return;
    }
    muOverlayStartedRef.current = true;
    setMuOverlayActive(true);
  }, [giantId, level?.modifierZone?.entered]);

  const onMuOverlayComplete = useCallback(() => {
    setMuOverlayActive(false);
  }, []);

  useEffect(() => {
    if (
      giantId !== 'yetti' ||
      level?.modifierZone?.entered !== true ||
      yettiOverlayStartedRef.current
    ) {
      return;
    }
    yettiOverlayStartedRef.current = true;
    setYettiOverlayActive(true);
  }, [giantId, level?.modifierZone?.entered]);

  const onYettiOverlayComplete = useCallback(() => {
    setYettiOverlayActive(false);
  }, []);

  useEffect(() => {
    if (
      giantId !== 'drakochi' ||
      level?.modifierZone?.entered !== true ||
      drakochiOverlayStartedRef.current
    ) {
      return;
    }
    drakochiOverlayStartedRef.current = true;
    setDrakochiOverlayActive(true);
  }, [giantId, level?.modifierZone?.entered]);

  const onDrakochiOverlayComplete = useCallback(() => {
    setDrakochiOverlayActive(false);
  }, []);

  const onThrustStart = useCallback(() => {
    if (tutorialHint === 'controls') {
      dismissTutorialHint();
    }
  }, [tutorialHint, dismissTutorialHint]);

  const onTutorialHintDismiss = useCallback(() => {
    dismissTutorialHint();
    if (!pendingSecondChanceAfterHintRef.current) {
      return;
    }
    pendingSecondChanceAfterHintRef.current = false;
    onSecondChanceRequestRef.current?.(buildSnapshotRef.current());
  }, [dismissTutorialHint]);

  const { getVerticalDirection, playfieldPointerHandlers } = useHoldFlightInput({
    enabled: inputEnabled,
    onThrustStart,
  });
  const getVerticalDirectionRef = useRef(getVerticalDirection);
  getVerticalDirectionRef.current = getVerticalDirection;

  const onSparksCollected = useCallback((count: number) => {
    collectiblesCollectedRef.current += count;
    setCollectiblesCollected(collectiblesCollectedRef.current);
    onCollectibleCollected();
  }, [onCollectibleCollected]);
  const onSpeedBoostersCollected = useCallback((count: number) => {
    speedBoostersCollectedRef.current += count;
    setSpeedBoostersCollected(speedBoostersCollectedRef.current);
  }, []);

  const getMotionScale = useCallback(() => motionScaleRef.current, []);
  const getDisplayTilt = useCallback(() => displayTiltRef.current, []);

  const getTickVerticalDirection = useCallback((): VerticalMoveDirection => {
    if (isFinishingRef.current) {
      return 0;
    }
    return getVerticalDirectionRef.current();
  }, []);

  const onFrame = useCallback((deltaSeconds: number) => {
    elapsedSecondsRef.current += deltaSeconds;

    const currentLevel = simLevelRef.current;
    const currentPlayer = simPlayerRef.current;
    if (currentLevel == null || currentPlayer == null || currentPlayer.lives <= 0) {
      return;
    }

    if (!isFinishingRef.current) {
      const speed = getRunnerForwardSpeed(currentLevel, currentPlayer);
      const remaining = currentLevel.routeEndS - currentPlayer.worldS;
      const brakingDistance = speed * FINISH_DECEL_SEC * 0.5;
      if (remaining <= brakingDistance) {
        isFinishingRef.current = true;
        finishElapsedRef.current = 0;
        finishTiltRef.current = displayTiltRef.current;
        if (!finishSoundPlayedRef.current) {
          finishSoundPlayedRef.current = true;
          playRunnerFinish();
        }
        setIsFinishing(true);
        const locked = { ...currentPlayer, targetLane: currentPlayer.lane };
        simPlayerRef.current = locked;
        setPlayer(locked);
      }
    }

    if (isFinishingRef.current) {
      finishElapsedRef.current += deltaSeconds;
      const t = Math.min(1, finishElapsedRef.current / FINISH_DECEL_SEC);
      const scale = 1 - t;
      motionScaleRef.current = scale;

      if (t >= 1 && !finishReportedRef.current) {
        finishReportedRef.current = true;
        if (currentPlayer.worldS < currentLevel.routeEndS) {
          const finished = { ...currentPlayer, worldS: currentLevel.routeEndS };
          simPlayerRef.current = finished;
          setPlayer(finished);
        }
        onSuccessRef.current(buildSnapshotRef.current());
      }
    }

    const targetTilt = isFinishingRef.current
      ? finishTiltRef.current * motionScaleRef.current
      : getVerticalDirectionRef.current();
    displayTiltRef.current = smoothPlayerTilt(
      displayTiltRef.current,
      targetTilt,
      deltaSeconds,
    );
  }, []);

  useRunnerLevelTick(
    level,
    player,
    setPlayer,
    setLevel,
    characterId,
    getTickVerticalDirection,
    onFrame,
    motionFrozen,
    getMotionScale,
    motionBindingsRef,
    getDisplayTilt,
    !motionFrozen,
    {
      onSparksCollected,
      onSpeedBoostersCollected,
      onObstacleHit,
      onBoosterCollected,
    },
    simLevelRef,
    simPlayerRef,
  );

  useEffect(() => {
    if (player == null) {
      return;
    }
    const prev = prevLivesRef.current;
    if (prev != null && player.lives < prev) {
      collisionCountRef.current += prev - player.lives;
    }
    prevLivesRef.current = player.lives;
  }, [player]);

  const buildSnapshot = useCallback((): LevelRunSnapshot => {
    return {
      elapsedSec: elapsedSecondsRef.current,
      sparksCollected: collectiblesCollectedRef.current,
      sparksTotal: layout.sparks.length,
      speedBoostersCollected: speedBoostersCollectedRef.current,
      speedBoostersTotal,
      collisions: collisionCountRef.current,
    };
  }, [layout.sparks.length, speedBoostersTotal]);
  buildSnapshotRef.current = buildSnapshot;

  useEffect(() => {
    onRunSnapshotUpdate?.(buildSnapshot());
  }, [
    collectiblesCollected,
    speedBoostersCollected,
    player?.lives,
    isFinishing,
    buildSnapshot,
    onRunSnapshotUpdate,
  ]);

  useEffect(() => {
    if (player == null) {
      return;
    }
    onHudUpdate({
      health: player.lives,
      maxHealth: player.maxLives,
      collectiblesCollected,
      collectiblesTotal: layout.sparks.length,
    });
  }, [
    player,
    collectiblesCollected,
    layout.sparks.length,
    onHudUpdate,
  ]);

  useEffect(() => {
    if (secondChanceRestoreKey === lastRestoreKeyRef.current) {
      return;
    }
    lastRestoreKeyRef.current = secondChanceRestoreKey;
    if (secondChanceRestoreKey <= 0) {
      return;
    }
    secondChanceUsedRef.current = true;
    secondChanceRequestedRef.current = false;
    failReportedRef.current = false;
    const current = simPlayerRef.current;
    if (current == null) {
      return;
    }
    const restored = {
      ...current,
      lives: current.maxLives,
      hitInvulnerableRemainingSeconds: Math.max(
        current.hitInvulnerableRemainingSeconds,
        1,
      ),
    };
    simPlayerRef.current = restored;
    setPlayer(restored);
  }, [secondChanceRestoreKey]);

  useEffect(() => {
    if (player == null) {
      return;
    }
    const decision = decideSecondChanceGate({
      lives: player.lives,
      secondChanceUsed: secondChanceUsedRef.current,
      alreadyRequested: secondChanceRequestedRef.current,
      alreadyFailed: failReportedRef.current,
    });
    if (decision == null) {
      return;
    }
    const snapshot = buildSnapshot();
    if (decision === 'request_minigame' && onSecondChanceRequestRef.current != null) {
      secondChanceRequestedRef.current = true;
      if (onLivesDepletedRef.current()) {
        pendingSecondChanceAfterHintRef.current = true;
        return;
      }
      onSecondChanceRequestRef.current(snapshot);
      return;
    }
    failReportedRef.current = true;
    onFailRef.current(snapshot);
  }, [player, buildSnapshot]);

  if (level == null || player == null) {
    return null;
  }

  return (
    <>
      <LevelPlayfield
        giantId={giantId}
        level={level}
        player={player}
        tilt={displayTilt}
        characterId={characterId}
        isPaused={motionFrozen}
        pointerHandlers={playfieldPointerHandlers}
        motionBindings={motionBindingsRef}
      />
      <div className="level-gameplay-contain" style={containStyle}>
        {countdownLabel != null ? (
          <LevelCountdownOverlay label={countdownLabel} />
        ) : null}
        {willyBlurActive ? (
          <LevelWillyBlurOverlay
            isPaused={isPaused}
            onComplete={onWillyBlurComplete}
          />
        ) : null}
        {monaTentacleActive ? (
          <LevelMonaTentacleOverlay
            isPaused={isPaused}
            onComplete={onMonaTentacleComplete}
          />
        ) : null}
        {duckBallActive ? (
          <LevelDuckBallOverlay
            isPaused={isPaused}
            onComplete={onDuckBallComplete}
          />
        ) : null}
        {rozaCloudActive ? (
          <LevelRozaCloudOverlay
            isPaused={isPaused}
            onComplete={onRozaCloudComplete}
          />
        ) : null}
        {serjOverlayActive ? (
          <LevelSerjOverlay
            isPaused={isPaused}
            onComplete={onSerjOverlayComplete}
          />
        ) : null}
        {svamiOverlayActive ? (
          <LevelSvamiOverlay
            isPaused={isPaused}
            onComplete={onSvamiOverlayComplete}
          />
        ) : null}
        {solOverlayActive ? (
          <LevelSolOverlay
            isPaused={isPaused}
            onComplete={onSolOverlayComplete}
          />
        ) : null}
        {factorOverlayActive ? (
          <LevelFactorOverlay
            isPaused={isPaused}
            onComplete={onFactorOverlayComplete}
          />
        ) : null}
        {muOverlayActive ? (
          <LevelMuOverlay
            isPaused={isPaused}
            onComplete={onMuOverlayComplete}
          />
        ) : null}
        {yettiOverlayActive ? (
          <LevelYettiOverlay
            isPaused={isPaused}
            onComplete={onYettiOverlayComplete}
          />
        ) : null}
        {drakochiOverlayActive ? (
          <LevelDrakochiOverlay
            isPaused={isPaused}
            onComplete={onDrakochiOverlayComplete}
          />
        ) : null}
      </div>
      {tutorialHint != null ? (
        <LevelTutorialHintOverlay
          hint={tutorialHint}
          capturePointer={tutorialHint !== 'controls'}
          onDismiss={onTutorialHintDismiss}
        />
      ) : null}
    </>
  );
}
